<template>
  <TemplateBase>
    <main class="relative z-0 col-span-full row-start-2" :class="{ 'xl:row-start-1': page.image }">
      <!-- MoleculeHeaderOverview -->
      <MoleculeHeaderOverview
        class="mb-8 sm:mb-12 md:mb-16"
        :dark-title="true"
        :position-text-left="true"
        :pre-title="page.preTitle"
        :title="page.title"
        :introduction="page.introduction"
        :image="page.image"
        :class="{ 'mt-16 lg:mt-20': !page.image }"
      >
        <template #headerButtons>
          <div v-if="page.references.length" class="flex-wrap mt-6 sm:flex lg:mt-8 xl:mt-10">
            <AtomButton
              v-for="(reference, index) in page.references"
              :key="index"
              button-type="primary"
              :url="reference.url"
              class="mb-3"
              :class="{ 'sm:ml-4 md:ml-6 lg:ml-8 xl:ml-10': index > 0 }"
            >
              {{ reference.title }}
              <AtomIconArrowRight class="ml-3 w-4" />
            </AtomButton>
          </div>
        </template>
      </MoleculeHeaderOverview>

      <!-- MoleculeHighlightedAnimals -->
      <MoleculeHighlightedAnimals
        class="container relative z-10 mb-16 mx-auto 2xl:px-56 px-6 sm:px-8"
        :animal-ids="page.animalHighlights"
        :title="page.animalTitle"
        :show-description="false"
        :url-label="page.buttonTitle"
        :url="page.buttonUrl"
      />

      <!-- Banner section -->
      <section v-if="page.banner">
        <MoleculeMatrixBanner
          :title="page.banner.title"
          :introduction="page.banner.introduction"
          :image="page.banner.image"
          :primary-button-label="page.banner.primaryButtonLabel"
          :primary-button-url="page.banner.primaryButtonUrl"
          :secondary-button-label="page.banner.secondaryButtonLabel"
          :secondary-button-url="page.banner.secondaryButtonUrl"
          class="col-span-full mb-12 mt-16 sm:mb-16 sm:mt-16"
          background-color="bg-app-green"
        />
      </section>

      <!-- Highlights section -->
      <section v-if="page.highlights.length" class="container relative z-10 mb-16 mx-auto 2xl:px-56 px-6 sm:px-8">
        <ul class="grid gap-y-4 grid-cols-1 mb-6 sm:gap-x-8 sm:gap-y-10 sm:grid-cols-2 lg:grid-cols-3 lg:mb-16">
          <li v-for="(item, index) in page.highlights" :key="index">
            <MoleculeCardHighlight :url="item.url" :title="item.title" :label="item.label" :image="item.image" :img-alt="item.title" />
          </li>
        </ul>
      </section>

      <!-- Image Slider Block section -->
      <section v-if="page.imagesSliderBlock" class="container relative z-10 mb-16 mx-auto 2xl:px-56 px-6 sm:px-8">
        <MoleculeHighlightedSliderWithText
          :label="page.imagesSliderBlock.label"
          :description="page.imagesSliderBlock.description"
          :url="get(page, 'imagesSliderBlock.reference.url')"
          :entries="page.imagesSliderBlock.children"
          :title="page.imageSliderBlockTitle"
          :button-label="page.imageSliderButtonLabel"
        />
      </section>

      <!-- Reference Block section -->
      <section v-if="page.referenceBlock" class="container relative z-10 mb-16 mx-auto 2xl:px-56 px-6 sm:px-8">
        <MoleculeHighlightBanner :image="page.referenceBlock.image">
          <h3 class="mb-3 text-xl font-bold uppercase sm:text-2xl lg:text-3xl">
            {{ page.referenceBlock.label }}
          </h3>
          <div v-if="page.referenceBlock.introduction" class="prose prose--app mb-10" v-html="page.referenceBlock.introduction" />
          <div class="inline-block">
            <AtomButton v-if="page.referenceBlock.buttonUrl" :url="page.referenceBlock.buttonUrl" button-type="primary" :full-width="false">
              {{ page.referenceBlock.buttonLabel }}
              <AtomIconArrowRight class="ml-3 w-3" />
            </AtomButton>
          </div>
        </MoleculeHighlightBanner>
      </section>

      <!-- Articles section -->
      <section v-if="page.articles.length" class="container relative z-10 mb-16 mx-auto 2xl:px-56 px-6 sm:px-8">
        <div class="justify-between mb-8 md:flex md:mb-6">
          <h2 class="mb-2 text-xl font-bold leading-8 uppercase sm:text-2xl md:mb-0 lg:text-3xl">{{ page.articleTitle || 'Verhalen uit het asiel' }}</h2>
          <AtomButton v-if="articleOverviewUrl" :url="articleOverviewUrl" button-type="primary" class="md:ml-auto">
            {{ t_('all-articles') }}
            <AtomIconArrowRight class="ml-3 w-4" />
          </AtomButton>
        </div>
        <MoleculeCarousel :slides="page.articles" type="slider">
          <template #slide="{ slide }">
            <OrganismLoaderFromUri v-slot="article" :site="slide.site" :uri="slide.uri">
              <MoleculeCarouselSlideStory :url="article.url" :title="article.title" :summary="article.introduction" :image="article.smallImage" />
            </OrganismLoaderFromUri>
          </template>
        </MoleculeCarousel>
      </section>

      <!-- Sponsor Images section -->
      <section v-if="page.sponsorImages.length" class="container relative z-10 mx-auto p-6 2xl:px-56 sm:px-8">
        <h2 class="block mb-8 text-xl font-bold leading-8 uppercase sm:text-2xl md:hidden lg:text-3xl">Wij worden gesteund door</h2>
        <div class="p-4 bg-white rounded-xl md:pb-8 md:pt-16 md:px-16">
          <h2 class="hidden mb-8 text-3xl font-bold leading-8 uppercase md:block">Wij worden gesteund door</h2>

          <div class="grid gap-6 grid-cols-2 md:grid-cols-5">
            <div v-for="(image, index) of page.sponsorImages" :key="index" class="flex items-center justify-center">
              <AtomLink
                :url="image.staticWebPage"
                class="inline-flex justify-center"
                :class="{ 'md:justify-center': index > 0 && index < page.sponsorImages.length - 1, 'md:justify-end': index === page.sponsorImages.length - 1 }"
              >
                <MoleculePicture :image="image" class="block" />
              </AtomLink>
            </div>
          </div>
        </div>
      </section>

      <!-- Donation Reference section -->
      <section v-if="page.donationReference && page.donationReference.url" class="container relative z-10 mb-16 mx-auto 2xl:px-56 px-6 sm:px-8">
        <div class="relative">
          <MoleculePicture :image="page.donationReference.image" class="cover-fill hidden w-full h-full lg:block" />
          <div class="bottom-0 right-0 w-full lg:absolute lg:max-w-xl">
            <MoleculeDonation
              :title="page.donationReference.title"
              :subtitle="page.donationReference.subtitle"
              :introduction="page.donationReference.introduction"
              :image="page.donationReference.image"
              :url="page.donationReference.url"
              :price1="page.donationReference.donationPrice1"
              :price2="page.donationReference.donationPrice2"
              :price3="page.donationReference.donationPrice3"
            />
          </div>
        </div>
      </section>
    </main>
  </TemplateBase>
</template>

<script setup lang="ts">
import get from 'lodash/get'
import { setupTranslationPrefixer } from '~/composables/util'
import { useGlobalStore } from '~/store/global'

interface Props {
  page: {
    image?: string
    preTitle: string
    title: string
    introduction: string
    references: Array<{ url: string; title: string }>
    animalHighlights: string[]
    animalTitle: string
    buttonTitle: string
    buttonUrl: string
    banner?: {
      title: string
      introduction: string
      image: string
      primaryButtonLabel: string
      primaryButtonUrl: string
      secondaryButtonLabel: string
      secondaryButtonUrl: string
    }
    highlights: Array<{ url: string; title: string; label: string; image: string }>
    imagesSliderBlock?: {
      label: string
      description: string
      reference?: { url: string }
      children: any[]
    }
    imageSliderBlockTitle?: string
    imageSliderButtonLabel?: string
    referenceBlock?: {
      image: string
      label: string
      introduction?: string
      buttonUrl?: string
      buttonLabel?: string
    }
    articles: any[]
    articleTitle?: string
    sponsorImages: Array<{ staticWebPage: string }>
    donationReference?: {
      url: string
      title: string
      subtitle: string
      introduction: string
      image: string
      donationPrice1: number
      donationPrice2: number
      donationPrice3: number
    }
  }
}

defineProps<Props>()

const globalStore = useGlobalStore()
const department = globalStore.department
const articleOverviewUrl = globalStore.articleOverview?.url

const getDepartmentDetails = (department: any) => {
  return {
    '@context': 'http://schema.org',
    '@type': ['LocalBusiness', 'AnimalShelter'],
    name: department.title,
    description: department.introduction,
    address: {
      '@type': 'PostalAddress',
      addressLocality: department.address.city,
      postalCode: department.address.postalCode,
      streetAddress: department.address.streetName,
    },
    image: {
      '@type': 'imageObject',
      url: `${process.env.wwwSiteUrl}/assets/common/logo-dierenbescherming.jpg`,
    },
    telephone: department.telephone,
    url: department.webPage,
    email: department.email,
    nonprofitStatus: 'NonprofitANBI',
    areaServed: {
      '@type': 'place',
      name: 'Nederland',
      '@id': 'http://www.wikidata.org/entity/Q29999',
    },
    brand: {
      '@type': 'Organization',
      name: 'Dierenbescherming',
      logo: {
        '@type': 'imageObject',
        url: `${process.env.wwwSiteUrl}/assets/common/logo-dierenbescherming.jpg`,
      },
    },
    parentOrganization: {
      '@type': 'Organization',
      name: 'Dierenbescherming',
      description: 'De Dierenbescherming beschermt dieren door het verlenen van (nood)hulp, het handhaven van welzijn en het beïnvloeden van de samenleving.',
      url: 'http://www.dierenbescherming.nl',
      telephone: '088 81 13 000',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Den Haag',
        postalCode: '2516 AC',
        streetAddress: 'Regulusweg 11',
      },
    },
    logo: {
      '@type': 'imageObject',
      url: `${process.env.wwwSiteUrl}/assets/common/logo-dierenbescherming.jpg`,
    },
    sameAs: [
      'https://www.facebook.com/deDierenbescherming',
      'https://www.instagram.com/dierenbescherming/',
      'https://twitter.com/dierbescherming',
      'https://www.linkedin.com/company/de-dierenbescherming',
    ],
  }
}

useJsonld(() => (department?.title ? getDepartmentDetails(department) : {}))

const { t_ } = setupTranslationPrefixer('landing-detail')
</script>
